import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import Icon from '@mdi/react';
import { getDataFromKey } from '../../utils';
import { useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { mdiLoading } from '@mdi/js';
import { useTranslate } from 'sbx-crm-menu-ui';
import { motion } from 'framer-motion';
import { Input } from '../ui/input';
import { PaginationV2 } from './PaginationV2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAmountDown, faSortAmountUp } from '@fortawesome/free-solid-svg-icons';

export interface ActionButton<DataType> {
    label: string;
    icon: string | JSX.Element;
    className?: string;
    onClick: (data: DataType) => void;
    visible?: (data: DataType) => boolean;
    disabled?: (data: DataType) => boolean;
    loading?: (data: DataType) => boolean;
}

export enum KeyHeader {
    KEY,
    VALUE,
}

interface MainHeader {
    label: string;
    visible?: boolean;
    sortable?: boolean;
    className?: string;
    absoluteKey?: string;
}

interface Header1<DataType> extends MainHeader {
    type: KeyHeader.KEY;
    key: keyof DataType | string;
}

interface Header2<DataType> extends MainHeader {
    type: KeyHeader.VALUE;
    toValue: (data: DataType) => JSX.Element | string;
}

export type Header<DataType> = Header1<DataType> | Header2<DataType>;

interface Props<DataType> {
    items: DataType[];
    actions?: ActionButton<DataType>[];
    pagination?: boolean;
    search?: boolean;
    count?: number;
    loading?: boolean;
    rowNavigation?: (data: DataType) => void;
    header: Header<DataType>[];
    topActions?: JSX.Element;
    filterActions?: JSX.Element;
    rowClassName?: string;
    tableClassName?: string;
    actionsClassName?: string;
    classNameContainer?: string;
    classNameTableContainer?: string;
    defaultSort?: {
        key: string;
        order: 'asc' | 'desc';
    };
}

const ITEMS_PER_PAGE = Math.floor(window.innerHeight / 100);
const classNameHeader = 'px-4 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider';

export default function TableComponentV2<DataType>({
    items,
    header,
    actions,
    pagination,
    search,
    count = ITEMS_PER_PAGE,
    loading,
    rowNavigation,
    topActions,
    filterActions,
    rowClassName = '',
    tableClassName = '',
    actionsClassName = '',
    classNameContainer = 'bg-white bg-opacity-80 backdrop-blur-lg rounded-3xl shadow-xl p-6 mb-8',
    classNameTableContainer = '',
    defaultSort,
}: Props<DataType>) {
    const [page, setPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const totalPages = Math.ceil(items.length / count);
    const { t } = useTranslate('common');

    function getValueFromHeadAndItem(item: DataType, head: Header<DataType>) {
        return head.type === KeyHeader.KEY ? getDataFromKey(item, head.key as string) : head.toValue(item);
    }

    const [sort, setSort] = useState(defaultSort);

    //filter with search input
    const itemsFiltered = useMemo(() => {
        return items.filter((item) => {
            if (!searchValue) return true;
            return header.some((head) => {
                const data = getValueFromHeadAndItem(item, head);
                return String(data).toLowerCase().includes(searchValue.toLowerCase());
            });
        });
    }, [searchValue, items]);

    // Pagination per page and count
    const itemsPerPagination = useMemo(() => {
        return itemsFiltered
            .sort((a, b) => {
                if (!sort) {
                    return 0;
                }

                const valueA = getDataFromKey(a, sort.key) as any;
                const valueB = getDataFromKey(b, sort.key) as any;
                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    return sort.order === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                }
                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return sort.order === 'asc' ? valueA - valueB : valueB - valueA;
                }
                return 0;
            })
            .slice((page - 1) * count, page * count);
    }, [page, itemsFiltered, sort]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Icon size={2} path={mdiLoading} className="text-primary" spin />
            </div>
        );
    }

    return (
        <div className={classNameContainer}>
            {search && (
                <div className="flex gap-2 md:gap-0 flex-col md:flex-row md:justify-between items-center mb-2">
                    <div className="relative md:w-1/3 w-full ">
                        <Input
                            type="text"
                            className={
                                'pl-10 pr-4 py-2 !border-purple-300 !focus:border-purple-500 focus:ring focus:!ring-purple-200 focus:ring-opacity-50 rounded-full'
                            }
                            onChange={(e) => setSearchValue(e.target.value)}
                            value={searchValue}
                            placeholder={t('search') + '...'}
                        />
                        <SearchIcon className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 right-2" />
                    </div>
                    {topActions}
                </div>
            )}

            {filterActions ? <div className="mb-4">{filterActions}</div> : null}

            <Table className={`w-full ${tableClassName}`} classNameTableContainer={classNameTableContainer}>
                <TableHeader>
                    <TableRow>
                        {header.map((head) => (
                            <TableHead
                                aria-checked={sort?.key === head.absoluteKey}
                                className={(head.className || classNameHeader).concat(
                                    ' aria-checked:text-primary text-nowrap'
                                )}
                                key={head.label}
                            >
                                {head.label}{' '}
                                {head.sortable && (
                                    <FontAwesomeIcon
                                        className="cursor-pointer "
                                        onClick={() => {
                                            setSort({
                                                key: head.absoluteKey as string,
                                                order:
                                                    sort?.key === head.absoluteKey
                                                        ? sort?.order === 'asc'
                                                            ? 'desc'
                                                            : 'asc'
                                                        : 'desc',
                                            });
                                        }}
                                        icon={
                                            sort?.key !== head.absoluteKey
                                                ? faSortAmountUp
                                                : sort?.order === 'asc'
                                                  ? faSortAmountUp
                                                  : faSortAmountDown
                                        }
                                    />
                                )}
                            </TableHead>
                        ))}
                        {actions && (
                            <TableHead
                                className={`text-right px-6 py-3  text-xs font-semibold text-purple-700 uppercase tracking-wider`}
                            >
                                {t('actions')}
                            </TableHead>
                        )}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {itemsPerPagination.map((item, index) => (
                        <motion.tr
                            key={`motion_${String((item as any)['_KEY']).concat(index.toString())}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => rowNavigation?.(item)}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className={
                                rowClassName ||
                                `border-purple-100 hover:bg-purple-50  transition-colors duration-200 border-b `
                            }
                        >
                            {/*<TableRow onClick={() => rowNavigation?.(item)}*/}
                            {/*          title={rowNavigation ? 'Click to view details' : ''}*/}
                            {/*          className={rowNavigation ? 'hover:bg-gray-100 cursor-pointer' : ''}*/}
                            {/*          key={String((item as any)['_KEY']).concat(index.toString())}>*/}
                            {header.map((head, index) => (
                                <TableCell key={index}>{getValueFromHeadAndItem(item, head)}</TableCell>
                            ))}
                            {actions && (
                                <TableCell>
                                    <div className={`${actionsClassName || 'flex justify-end gap-2'}`}>
                                        {actions
                                            .filter((action) => (action.visible ? action.visible(item) : true))
                                            .map((action) => (
                                                <button
                                                    key={action.label}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        action.onClick(item);
                                                    }}
                                                    type="button"
                                                    className={action.className ?? ''}
                                                    title={action.label}
                                                >
                                                    {typeof action.icon === 'string' ? (
                                                        <Icon size={0.6} path={action.icon} />
                                                    ) : (
                                                        action.icon
                                                    )}
                                                </button>
                                            ))}
                                    </div>
                                </TableCell>
                            )}
                            {/*</TableRow>*/}
                        </motion.tr>
                    ))}
                </TableBody>
            </Table>

            {!itemsFiltered.length && (
                <div className="flex justify-center items-center w-full h-[30vh] bg-gray-50 rounded">
                    <b className="text-gray-400">La tabla esta vacia</b>
                </div>
            )}

            {pagination && itemsFiltered.length > count && (
                <PaginationV2 page={page} setPage={setPage} totalPages={totalPages} rowCount={items.length} />
            )}
        </div>
    );
}
